import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LogOut, Sparkles, ShieldCheck, Moon, Bell, FileText } from "lucide-react";
import { subscribePush, unsubscribePush, isPushSubscribed } from "@/lib/push";
import { trackEvent } from "@/lib/analytics";
import api from "@/lib/api";

export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { isPushSubscribed().then(setPushOn); }, []);

  // After Cashfree checkout, the user returns to /settings?cf_order_id=...
  // Verify the payment with the backend and refresh the user state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("cf_order_id");
    if (!orderId) return;
    (async () => {
      try {
        const { data } = await api.post("/subscriptions/verify", { order_id: orderId });
        if (data.paid) {
          trackEvent("purchase", {
            transaction_id: orderId,
            value: data.plan_key === "yearly" ? 1999 : 199,
            currency: "INR",
            items: [{ item_id: data.plan_key, item_name: `ResyNote ${data.plan_key}` }],
          });
          toast.success("Payment received — ResyNote Premium activated.");
          setTimeout(() => window.location.replace("/settings"), 1200);
        } else {
          toast.info(`Payment ${data.order_status?.toLowerCase() || "pending"}. We'll update once it clears.`);
          window.history.replaceState({}, "", "/settings");
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not verify payment");
      }
    })();
  }, []);

  const togglePush = async (v) => {
    setPushBusy(true);
    try {
      if (v) {
        await subscribePush();
        setPushOn(true);
        toast.success("Push notifications enabled");
      } else {
        await unsubscribePush();
        setPushOn(false);
        toast.success("Push notifications disabled");
      }
    } catch (e) {
      toast.error(e.message || "Could not update push settings");
      setPushOn(await isPushSubscribed());
    } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test notification sent");
    } catch {
      toast.error("Failed to send test");
    }
  };

  const generateWeekly = async () => {
    try {
      const { data } = await api.post("/reports/weekly/generate");
      if (!data.generated?.length) {
        toast.info("No labs to report on yet — create a lab first.");
      } else {
        toast.success(`Generated ${data.generated.length} weekly report(s). Check Notifications.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate report");
    }
  };

  const trialEnd = user?.trial_end ? new Date(user.trial_end) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="settings-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Account</div>
        <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-1">Settings</h1>
      </div>

      {/* Profile */}
      <Section title="Profile">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 grid place-items-center overflow-hidden">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="font-sans font-bold">{(user?.name || "U").slice(0, 1)}</span>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-sans font-semibold">{user?.name || "Researcher"}</div>
            <div className="text-sm font-mono text-zinc-500 truncate">{user?.email}</div>
          </div>
        </div>
      </Section>

      {/* Subscription */}
      <Section title="Subscription">
        <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-5 bg-white dark:bg-zinc-900 space-y-4">
          {user?.subscription_active ? (
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="font-sans font-semibold">Pro · Active</div>
                <div className="text-sm font-body text-zinc-500">Thanks for supporting ResyNote.</div>
              </div>
            </div>
          ) : daysLeft > 0 ? (
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-blue-600" />
              <div>
                <div className="font-sans font-semibold">Free trial · {daysLeft} day{daysLeft !== 1 ? "s" : ""} left</div>
                <div className="text-sm font-body text-zinc-500">All premium features unlocked.</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-red-600" />
              <div>
                <div className="font-sans font-semibold">Trial ended</div>
                <div className="text-sm font-body text-zinc-500">Upgrade to continue with full access.</div>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-zinc-200 dark:border-zinc-800">
            <Plan name="Monthly" price="₹199" alt="$2.49 · €2.29" sub="per month" featured={false} />
            <Plan name="Annual" price="₹1,999" alt="$23.99 · €21.99" sub="~16% off · best value" featured />
          </div>

          {user?.subscription_active ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={async () => {
                if (!window.confirm("Cancel subscription? Access continues until the end of your billing period.")) return;
                try {
                  await api.post("/subscriptions/cancel");
                  toast.success("Subscription cancelled");
                  setTimeout(() => window.location.reload(), 800);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not cancel");
                }
              }}
              data-testid="cancel-subscription-btn"
            >
              Cancel subscription
            </Button>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <UpgradeButton plan="monthly" label="Subscribe monthly" />
              <UpgradeButton plan="yearly" label="Subscribe yearly" featured />
            </div>
          )}
        </div>
      </Section>

      {/* Preferences */}
      <Section title="Preferences">
        <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-5 bg-white dark:bg-zinc-900 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-sans font-medium">Dark mode</div>
              <div className="text-xs font-mono text-zinc-500">Easier on the eyes during late-night sessions.</div>
            </div>
            <div className="flex items-center gap-2">
              <Moon className="h-4 w-4 text-zinc-400" />
              <Switch
                checked={theme === "dark"}
                onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
                data-testid="dark-mode-toggle"
              />
            </div>
          </div>

          <div className="h-px bg-zinc-200 dark:bg-zinc-800" />

          <div className="flex items-center justify-between">
            <div>
              <div className="font-sans font-medium">Push notifications</div>
              <div className="text-xs font-mono text-zinc-500">
                Reminders at the exact time + a morning digest at 9 AM.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-zinc-400" />
              <Switch
                checked={pushOn}
                onCheckedChange={togglePush}
                disabled={pushBusy}
                data-testid="push-toggle"
              />
            </div>
          </div>
          {pushOn && (
            <Button size="sm" variant="outline" onClick={testPush} data-testid="send-test-push">
              Send test notification
            </Button>
          )}
        </div>
      </Section>

      {/* Danger */}
      <Section title="Account">
        {user?.account_role && (
          <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4 bg-white dark:bg-zinc-900 mb-4 flex items-center justify-between" data-testid="account-role-block">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Account role</div>
              <div className="font-sans font-semibold capitalize mt-1">{user.account_role}</div>
            </div>
            {user.account_role === "supervisor" && (
              <Button variant="outline" size="sm" onClick={generateWeekly} data-testid="generate-weekly-btn">
                <FileText className="h-4 w-4 mr-2" /> Generate weekly report
              </Button>
            )}
          </div>
        )}
        <Button variant="outline" className="hover:text-red-600 hover:border-red-400" onClick={logout} data-testid="sign-out-btn">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </Section>

      {/* About / Contact */}
      <Section title="About">
        <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-5 bg-white dark:bg-zinc-900 space-y-3">
          <div>
            <div className="font-sans font-semibold">ResyNote</div>
            <div className="text-sm font-body text-zinc-600 dark:text-zinc-400 mt-1">
              Owned & operated by <span className="font-semibold">THREENNOVATORS LLP</span>
            </div>
          </div>
          <div className="h-px bg-zinc-200 dark:bg-zinc-800" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Contact us</div>
            <a
              href="mailto:hello@resynote.com"
              className="font-sans font-medium text-blue-600 hover:underline"
              data-testid="settings-contact-email"
            >
              hello@resynote.com
            </a>
          </div>
          <div className="text-xs font-mono text-zinc-500">© 2026 THREENNOVATORS LLP</div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">{title}</div>
      {children}
    </section>
  );
}

function Plan({ name, price, alt, sub, featured }) {
  return (
    <div className={`p-4 rounded border ${featured ? "border-blue-600 bg-blue-50/40 dark:bg-blue-950/20 dark:border-blue-500" : "border-zinc-200 dark:border-zinc-800"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{name}</div>
      <div className="font-sans font-bold text-2xl mt-1" data-testid={`plan-price-${name.toLowerCase()}`}>{price}</div>
      {alt && (
        <div className="text-[11px] font-mono text-zinc-500 mt-0.5" data-testid={`plan-alt-${name.toLowerCase()}`}>{alt}</div>
      )}
      <div className="text-xs font-mono text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}

function UpgradeButton({ plan, label, featured }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try {
      trackEvent("begin_checkout", {
        currency: "INR",
        value: plan === "yearly" ? 1999 : 199,
        items: [{ item_id: plan, item_name: `ResyNote ${plan}` }],
      });
      const returnUrl = `${window.location.origin}/settings`;
      const { data } = await api.post("/subscriptions/checkout", {
        plan_key: plan,
        return_url: returnUrl,
      });
      const sessionId = data.payment_session_id;
      const mode = data.mode || "production";
      if (!sessionId) {
        toast.error("Checkout session missing — please contact support.");
        return;
      }
      // Lazy-load the Cashfree JS SDK and open hosted checkout.
      const cashfree = await loadCashfreeSdk(mode);
      if (!cashfree) {
        toast.error("Could not load payment SDK. Please refresh and try again.");
        return;
      }
      cashfree.checkout({
        paymentSessionId: sessionId,
        redirectTarget: "_self",
      });
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not start checkout";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={handleClick}
      disabled={busy}
      className={featured ? "w-full bg-blue-600 hover:bg-blue-700 text-white" : "w-full"}
      variant={featured ? "default" : "outline"}
      data-testid={`upgrade-cta-${plan}`}
    >
      {busy ? "Starting checkout…" : label}
    </Button>
  );
}

// Loads the Cashfree v3 SDK exactly once and returns an initialized instance.
let _cashfreePromise = null;
function loadCashfreeSdk(mode) {
  if (_cashfreePromise) return _cashfreePromise;
  _cashfreePromise = new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.Cashfree) {
      try { resolve(window.Cashfree({ mode })); } catch { resolve(null); }
      return;
    }
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.async = true;
    s.onload = () => {
      try { resolve(window.Cashfree({ mode })); } catch { resolve(null); }
    };
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return _cashfreePromise;
}
